import { useState, useEffect, useCallback } from 'react'
import type { ApiResponse } from '../types'

const API_BASE = import.meta.env.VITE_API_URL || 'https://deployment-manager-api.aubrey-smith.workers.dev/api'

export function useApi<T>(endpoint: string, options?: RequestInit) {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await fetch(`${API_BASE}${endpoint}`, {
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0',
          ...options?.headers,
        },
        cache: 'no-store',
        ...options,
      })

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`API request failed: ${response.status} ${response.statusText}. ${errorText}`)
      }

      const result: ApiResponse<T> = await response.json()

      if (!result.success) {
        throw new Error(result.error || 'API request failed')
      }

      setData(result.data || null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }, [endpoint, options])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return { data, loading, error, refetch: fetchData }
}

export function useApiMutation<TData, TVariables = any>() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const mutate = useCallback(async (
    endpoint: string,
    variables?: TVariables,
    options?: RequestInit
  ): Promise<TData | null> => {
    try {
      setLoading(true)
      setError(null)

      const response = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0',
          ...options?.headers,
        },
        cache: 'no-store',
        body: variables ? JSON.stringify(variables) : undefined,
        ...options,
      })

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`API request failed: ${response.status} ${response.statusText}. ${errorText}`)
      }

      const result: ApiResponse<TData> = await response.json()

      if (!result.success) {
        throw new Error(result.error || 'API request failed')
      }

      return result.data || null
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      setError(errorMessage)
      throw new Error(errorMessage)
    } finally {
      setLoading(false)
    }
  }, [])

  return { mutate, loading, error }
}

// Polling hook for real-time updates
export function usePolling<T>(
  endpoint: string,
  interval: number = 30000, // 30 seconds default
  options?: RequestInit
) {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    try {
      if (loading) setError(null) // Only clear error on initial load
      
      const response = await fetch(`${API_BASE}${endpoint}`, {
        headers: {
          'Content-Type': 'application/json',
          ...options?.headers,
        },
        ...options,
      })

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`API request failed: ${response.status} ${response.statusText}. ${errorText}`)
      }

      const result: ApiResponse<T> = await response.json()

      if (!result.success) {
        throw new Error(result.error || 'API request failed')
      }

      setData(result.data || null)
      setError(null) // Clear error on successful fetch
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }, [endpoint, options, loading])

  useEffect(() => {
    fetchData()
    
    const intervalId = setInterval(fetchData, interval)
    
    return () => clearInterval(intervalId)
  }, [fetchData, interval])

  return { data, loading, error, refetch: fetchData }
}